import { useEffect, useRef, useState } from "react";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
  Dialog as MUIDialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Autocomplete,
  Grid,
  Box,
  Typography,
  IconButton,
} from "@mui/material";
import { TimeField } from "component/atoms/time-field";
// import { zohoApi } from "zohoApi";
import { Controller, useForm } from "react-hook-form";
import { useEventConfigArray } from "hook/use-event-config-array";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
// import { useMutation, useQueryClient } from "@tanstack/react-query";
// import { enqueueSnackbar } from "notistack";
import { useUpdateEventTime } from "hook/use-update-event-time";

dayjs.extend(utc);
dayjs.extend(timezone);

export function EditDialog({ dialogOpen, handleCloseDialog, selectedEvent, handleOpenDeleteDialog, refetchData }) {
  const editView = true;
  const mutate = useUpdateEventTime();
  const settingArray = useEventConfigArray();
  const [selectedSetting, setSelectedSetting] = useState(null);
  //
  const onlyDate =
    selectedSetting?.allDay || selectedSetting?.startTimeField?.data_type === "date" || selectedSetting?.endTimeField?.data_type === "date";

  const selectedActivity = settingArray?.find((el) => el?.setting_id === selectedEvent.setting_id);

  const firstRender = useRef(true);
  useEffect(() => {
    if (selectedActivity && firstRender.current) {
      firstRender.current = false;
      setSelectedSetting(selectedActivity);
    }
  }, [selectedActivity]);

  const defaultValues = selectedActivity?.popupFields?.reduce((acc, field) => {
    const key = field?.display_label;
    const val = selectedEvent[field?.api_name];
    acc[key] = val;
    return acc;
  }, {});

  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    watch,
    formState: { dirtyFields },
    reset,
  } = useForm({
    defaultValues: {
      id: selectedEvent.id,
      moduleApiName: selectedEvent.moduleApiName,
      selectedActivity,
      startTime: dayjs(selectedEvent?.start),
      endTime: dayjs(selectedEvent?.end),
      ...defaultValues,
    },
  });

  const onSubmit = async (data) => {
    mutate(data);
    handleCloseDialog();
  };

  return (
    <MUIDialog
      open={dialogOpen}
      onClose={handleCloseDialog}
      fullWidth={true}
      slotProps={{
        paper: {
          component: "form",
          onSubmit: handleSubmit(onSubmit),
        },
      }}
      sx={{
        "& .MuiDialog-container": {
          alignItems: "flex-start",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <a style={{ textDecoration: "underline dotted" }} href={selectedEvent?.recordUrl} target={selectedEvent?.recordUrl}>
          <span
            style={{
              display: "block",
              maxWidth: "100%",
              overflow: "hidden",
              whiteSpace: "nowrap",
              textOverflow: "ellipsis",
            }}
          >
            {selectedEvent?.title ?? "Create New Event"}
          </span>
        </a>
        {selectedEvent?.deletable ? (
          <IconButton
            aria-label="delete"
            size="small"
            color="error"
            onClick={async () => {
              handleCloseDialog();
              handleOpenDeleteDialog(selectedEvent);
            }}
          >
            <DeleteOutlineIcon />
          </IconButton>
        ) : null}
      </DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
        <Grid container sx={{ pt: "1px" }}>
          <Grid size={4}>
            <Typography component="label" htmlFor="module-input" sx={{ color: "text.secondary" }}>
              Select Activity
            </Typography>
          </Grid>
          <Grid size={8}>
            <Controller
              name="selectedActivity"
              control={control}
              render={({ field }) => {
                const { onChange, ...rest } = field;
                return (
                  <Autocomplete
                    {...rest}
                    id="selected-activity-input"
                    filterSelectedOptions
                    disabled={true}
                    // disabled={!settingArray}
                    options={settingArray}
                    getOptionLabel={(option) => option?.activity || ""}
                    onChange={(e, value, reason) => {
                      setSelectedSetting(value);
                      onChange(value);
                    }}
                    size="small"
                    renderInput={(params) => <TextField {...params} required placeholder="Select Activity" />}
                  />
                );
              }}
            />
          </Grid>
        </Grid>

        {selectedSetting?.popupFields?.length > 0 ? (
          <TimeField
            disabled={!selectedSetting}
            Controller={Controller}
            control={control}
            onlyDate={onlyDate}
            watch={watch}
            selectedActivity={selectedActivity}
            editView={editView}
            setValue={setValue}
          />
        ) : null}

        {selectedSetting?.popupFields?.map((el) => (
          <Grid container key={el?.display_label}>
            <Grid size={4}>
              <Typography component="label" htmlFor={el?.display_label} sx={{ color: "text.secondary" }}>
                {el?.display_label}
              </Typography>
            </Grid>
            <Grid size={8}>
              <Controller
                name={el?.display_label}
                disabled={true}
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    required
                    sx={{ width: "100%" }}
                    id={el?.display_label}
                    placeholder={el?.display_label}
                    variant="standard"
                  />
                )}
              />
            </Grid>
          </Grid>
        ))}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => {
            handleCloseDialog();
            setSelectedSetting(null);
            reset();
          }}
          size="small"
          variant="outlined"
          sx={{ textTransform: "capitalize" }}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          size="small"
          variant="contained"
          sx={{ textTransform: "capitalize" }}
          disabled={!selectedSetting || !(dayjs(watch("startTime")).isValid() && dayjs(watch("endTime")).isValid())}
        >
          Save
        </Button>
      </DialogActions>
    </MUIDialog>
  );
}
